import '../inputs/buttons/as-icon-button.ts';
import { TrSessionSet } from './subcomps/as-session-set.ts';
import './subcomps/as-view-set';
import './subcomps/as-state-set';
import '../overlays/as-popout-button';
import '../text-and-lang/as-text';
import '../base/as-spinner';
import '../overlays/as-tooltip-wrapper.ts';
import { hasEnabledAttribute, createElWithAttrs, replaceChildren } from '../../util/wcUtil.ts';
import { _registerSingleton, _unregisterSingleton, asApp } from '../../util/singletons.ts';
import type { TrStateSet } from './subcomps/as-state-set';
import type { TrViewSet, ViewButtonName } from './subcomps/as-view-set';
import { internalData } from '../../store/store.ts';
import { getSysData } from '../../store/sysDataStore.ts';
import { bindElToSysData, applyLabelVars } from '../base.ts';
import { iconPath } from '../../data/icons.ts';
import { logOut, getUserData } from '../../features/serverApi/asApi.ts';
import { getLicenseInfo } from './lib/license.ts';
import { devlog } from '../../util/utils.ts';
import { fullAppSuiteRootURL, isOnPrem, HIDE_LICENSE_UX, isProduction, SHOW_LANG_CTRLS } from '../../data/constants.ts';
import { getAppSettings } from '../../data/appSettings.ts';

import styleText from './as-header.css?raw';

// Source - https://stackoverflow.com/a
// Posted by Harshal Patil
// Retrieved 2025-11-10, License - CC BY-SA 4.0

// Read SCSS file as a raw CSS text using Webpack/Rollup/Parcel

const sheet = new CSSStyleSheet();
sheet.replaceSync(styleText);

/**
 * The panel behind the header's user-options button: greeting, email, license
 * and the two account links.
 *
 * Light DOM on purpose. It is slotted into the popout card, and the header's
 * stylesheet (the #userOptionsPanel rules) only reaches it while it stays in
 * the header's shadow tree. Owning its own listeners is the point of the
 * split: the content is rebuilt on every auth update, and nothing outside has
 * to notice or re-attach anything.
 */
export class TrUserOptions extends HTMLElement {
  static readonly tag = 'as-user-options' as const;

  private _isSigningOut = false;

  connectedCallback() {
    window.addEventListener('as-auth-update', this._onAuthUpdate);
    this.addEventListener('click', this._onClick);
    this.render();
    // Two auth paths (local dev, noAPI) fill the store without announcing it,
    // so ask once rather than wait for an event that never comes. getUserData
    // dedupes, so this costs no extra request.
    if (!internalData.userData) void getUserData().then(() => this.render());
  }

  disconnectedCallback() {
    window.removeEventListener('as-auth-update', this._onAuthUpdate);
  }

  render(): void {
    this.innerHTML = this._panelHTML();
    // The name and the licence label are values, so they ride in as vars
    // rather than being baked into an English sentence.
    const greeting = this.querySelector<HTMLElement>('#userGreetingText');
    if (greeting) applyLabelVars(greeting, [this._userName()]);
    const licence = this.querySelector<HTMLElement>('#licenseLabel');
    if (licence) applyLabelVars(licence, this._licenceVars);
  }

  /** The display name, or the generic stand-in when the user has no name set. */
  private _userName(): string {
    const user = internalData.userData;
    if (!user) return '';
    const name = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'User';
    return user.organization ? `${name} (${user.organization})` : name;
  }

  private _onAuthUpdate = (event: Event): void => {
    devlog('Auth update received:', (event as CustomEvent).detail);
    this.render();
  };

  /** One delegated handler, so a re-render never leaves a dead link behind.
   *  The click stops here: it would otherwise reach the popout button and
   *  toggle the card shut. */
  private _onClick = (event: Event): void => {
    const action = (event.target as Element | null)?.closest('[data-action]')?.getAttribute('data-action');
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    if (action === 'sign-out') void this._signOut();
    else if (action === 'manage-account') window.open(`${fullAppSuiteRootURL}/?modal=manage-user-view`, '_blank');
  };

  private async _signOut(): Promise<void> {
    this._isSigningOut = true;
    this.render();
    try {
      await logOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      this._isSigningOut = false;
      this.render();
    }
  }

  /** Placeholders stand in until the user data lands. */
  private _panelHTML(): string {
    const user = internalData.userData;

    const greeting = !user
      ? /*html*/`<as-text dict-key="userGreeting.loading" default-text="Hello"></as-text> <span class="placeholder name"></span>`
      : /*html*/`<as-text id="userGreetingText" dict-key="userGreeting" default-text="Hello {0}"></as-text>`;

    const email = !user ? '<span class="placeholder email"></span>' : user.eMail || '';

    const signOut = this._isSigningOut
      ? '<as-spinner style="width: 1rem; height: 1rem;"></as-spinner>'
      : '<as-text dict-key="signOutLink">Sign Out</as-text>';

    return /*html*/`
      <div id="userOptionsPanel">
        <span id="userGreeting" style="padding-bottom: 1rem;">${greeting}</span>

        ${email ? /*html*/`<span id="userEmail">${email}</span>` : ''}

        ${this._licenseHTML()}

        <div style="display: flex; flex-direction: row;">
          <a id="manage-account-button" href="#" data-action="manage-account">
            <as-text dict-key="manageAccountLink">Manage Account</as-text>
          </a>
        </div>

        <a id="signOutLink" href="#" data-action="sign-out">${signOut}</a>
      </div>
    `;
  }

  /** Vars for the licence label, set by render(). The type name comes from the
   *  server, so it is a value in the sentence rather than part of the key. */
  private _licenceVars: any[] = [];

  /** No license line while the data is still loading, and none at all in
   *  production while the license UX is hidden. */
  private _licenseHTML(): string {
    const user = internalData.userData;
    if (!user || (HIDE_LICENSE_UX && isProduction)) return '';

    const info = getLicenseInfo(user.license);
    this._licenceVars = info.labelVars;
    const warn = info.isWarning ? ' class="license-warning"' : '';
    return /*html*/`<span id="licenseInfo"${warn}><as-text dict-key="license.label">License:</as-text> <as-text id="licenseLabel" dict-key="${info.labelDictKey}" default-text="${info.label}"></as-text></span>`;
  }
}

customElements.define(TrUserOptions.tag, TrUserOptions);

/**
 * Main header component that composes all section components
 */
export class TrHeader extends HTMLElement {
  static readonly tag = 'as-header' as const;

  // private readonly headerImgDir = new URL('assets/img/header/', import.meta.url).pathname ;
  private readonly _headerImgDir = 'assets/img/header/'
  private _cleanedTitle = 'App';
  private _envObserver: MutationObserver | null = null;

  /** Per-selector visibility for icon-group children (e.g. as-view-set), set via
   *  setIconGroupVisible(). Kept here (not just applied once) because render()
   *  replaces the whole shadow DOM on unrelated attribute churn (app-title,
   *  env/clickthru-url mutations) — without reapplying after each render, a
   *  caller's inline opacity/pointer-events would be lost on the fresh element
   *  and silently revert to the CSS default (fully visible). */
  private _groupVisibility = new Map<string, boolean>();

  static get observedAttributes(): string[] {
    return ['app-title','no-session-icons','no-view-icons','no-state-icons','no-default-icons'];
  }

  get projectTitle(): string {
    return (this.shadowRoot?.querySelector('as-session-set') as TrSessionSet | null)?.projectTitle ?? '';
  }

  set projectTitle(value: string) {
    const sessionSet = this.shadowRoot?.querySelector('as-session-set') as TrSessionSet | null;
    if (sessionSet) sessionSet.projectTitle = value;
  }

  /** The project-name text input inside the session icon group, for apps that
   *  want to listen to it directly. Null when the session icons are hidden.
   *  Prefer the `as-project-title-change` event, which bubbles out of the header. */
  get projectTitleInput(): HTMLInputElement | null {
    return (this.shadowRoot?.querySelector('as-session-set') as TrSessionSet | null)?.projectTitleInput ?? null;
  }

  /** Enable or disable the header's undo/redo buttons. Only meaningful when
   *  `<as-app>` has `disable-undo-history` and the app runs its own history;
   *  otherwise the built-in stack owns them. No-op when the state icons are hidden. */
  setUndoRedoState(state: { canUndo?: boolean; canRedo?: boolean }): void {
    (this.shadowRoot?.querySelector('as-state-set') as TrStateSet | null)?.setUndoRedoState(state);
  }

  /** Enable or disable the camera buttons in the view set: all of them, or
   *  only those named in `names`. `setViewButtonsEnabled(false, ['view-top'])`
   *  disables one button. The names are `toggle-perspective`, `view-fit`,
   *  `view-iso`, `view-front`, `view-right` and `view-top`. No-op when the
   *  view icons are hidden. */
  setViewButtonsEnabled(enabled: boolean, names?: ViewButtonName[]): void {
    (this.shadowRoot?.querySelector('as-view-set') as TrViewSet | null)?.setEnabled(enabled, names);
  }


  /** While the app is busy (configure/reinit/load in flight), gate the header
   *  actions that would mutate the session mid-operation: session set
   *  (new/restore/save) and state set (undo/redo). `inert` blocks pointer AND
   *  keyboard/focus without touching the subcomponents' own internal disabled
   *  logic (undo/redo enablement), which shows through again on release.
   *  View controls (camera) stay usable — adjusting the view while waiting is
   *  fine — and the home button keeps working: the close flow has its own
   *  busy-waiting (see close-wait-busy).
   */
  private _unbindBusy: (() => void) | null = null;

  private _applyBusyGate(busy = getSysData().busy): void {
    this.toggleAttribute('busy', busy);
    for (const sel of ['as-session-set', 'as-state-set']) {
      const el = this.shadowRoot?.querySelector<HTMLElement>(sel);
      if (el) el.inert = busy;
    }
  }

  /**
   * Fade an icon-group child (e.g. as-view-set) in/out independently of the
   * others. Unlike the CSS --icon-set-opacity var (which drives every
   * icon-group at once), this targets one selector — apps can show/hide a
   * single control cluster (e.g. camera controls only during an export step)
   * without touching the rest. Survives re-renders: see _applyGroupVisibility.
   */
  setIconGroupVisible(selector: string, visible: boolean): void {
    this._groupVisibility.set(selector, visible);
    this._applyGroupVisibility();
  }

  private _applyGroupVisibility(): void {
    for (const [selector, visible] of this._groupVisibility) {
      const el = this.shadowRoot?.querySelector<HTMLElement>(selector);
      if (!el) continue;
      el.style.transition = 'opacity 0.25s ease';
      el.style.opacity = visible ? '1' : '0';
      el.style.pointerEvents = visible ? 'auto' : 'none';
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    if (name !== 'app-title') {
      this.render();
      return;
    }

    this._cleanedTitle = newValue?.trim() ? newValue : 'App';
    const titleEl = this.shadowRoot?.querySelector('#header-title');
    if (!titleEl) {
      this.render();
      return;
    }
    // Update the title in place rather than re-render, to avoid layout
    // flicker. The constructor rendered before this callback ran, so the
    // as-text took the placeholder as its default-text when it connected.
    // The title is not a dict key, so every re-send from the dictionary
    // falls back to that attribute: it has to follow the title too.
    titleEl.textContent = this._cleanedTitle;
    titleEl.setAttribute('default-text', this._cleanedTitle);
  }

  render(){
    this.shadowRoot!.innerHTML = /*html*/`
    <div class="header-content">
      <div class="header-left">
        <as-icon-button
          name="home"
          black
          file-name="header/apps.png"
          tooltip-dict-key="tooltip.apps.home"
          tooltip-default-text="Back to App Suite"
          tooltip-placement="bottom-right"
        ></as-icon-button>
        <as-text id="header-title" dict-key="${this.getAttribute('app-title') || ''}" default-text="${this._cleanedTitle}">${this._cleanedTitle}</as-text>
      </div>

      <slot name="slot-0"></slot>
      ${hasEnabledAttribute(this, 'no-session-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-session-set class="icon-group"></as-session-set>`}
      <slot name="slot-1"></slot>
      ${hasEnabledAttribute(this, 'no-view-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-view-set class="icon-group"></as-view-set>`}
      <slot name="slot-2"></slot>
      ${hasEnabledAttribute(this, 'no-state-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-state-set class="icon-group"></as-state-set>`}
      <slot name="slot-3"></slot>

      <div class="header-right">
        ${SHOW_LANG_CTRLS ? `<as-lang-selector label-default-text="Select Language" label-dict-key="langSelector"></as-lang-selector>` : ''}

        <!-- Clickthrough help link is projected here from light DOM (see
             _updateHelpLink) so its <a target="_blank"> is reachable by the
             GrabCAD host's link interception — a shadow-DOM anchor is retargeted
             to the host element and never opens. -->
        <slot name="help-link"></slot>

        <!-- Filled by _applyEnvSettings(), which is also what updates it when
             env changes — see there for why this isn't inlined above. -->
        <div class="user-options"></div>
      </div>
    </div>
  `;
    this._applyEnvSettings();
    this._applyBusyGate();
    this._applyGroupVisibility();
  }

  /**
   * The parts of the header that depend on the resolved env, updated in place.
   *
   * These used to live in render(), with a MutationObserver on `<as-app>`'s
   * `env` / `clickthru-url` calling render() again whenever they changed. `env`
   * resolves asynchronously, so that second render was all but guaranteed — and
   * every one of them reassigned `shadowRoot.innerHTML`, destroying and
   * rebuilding every custom element in the header. Anything holding state (the
   * language selector most visibly) lost it, listeners were rebuilt, and the
   * icon groups had their busy gate and visibility re-applied to fresh
   * elements. Now render() builds the markup once and this updates the two
   * things that actually vary.
   */
  private _applyEnvSettings(): void {
    const envSettings = getAppSettings(asApp()?.getAttribute('env') ?? null);

    // The selector stays put; only its disabled state follows the env.
    const langSelector = this.shadowRoot!.querySelector('as-lang-selector');
    if (envSettings.allowLangSelector) langSelector?.removeAttribute('disabled');
    else langSelector?.setAttribute('disabled', '');

    // A rebuild of one <div>, and only when the state actually changed. On-prem
    // has no accounts, so it shows nothing at all.
    const slot = this.shadowRoot!.querySelector<HTMLElement>('.user-options');
    const wanted = !isOnPrem && envSettings.showUserOptions ? 'popout' : 'none';
    if (slot && slot.dataset.state !== wanted) {
      slot.dataset.state = wanted;
      replaceChildren(slot, wanted === 'popout' ? this._createUserPopout() : []);
    }

    this._updateHelpLink();
  }

  /** The real user-options button. The panel inside it keeps itself current,
   *  so this only has to place it. */
  private _createUserPopout(): HTMLElement {
    const btn = createElWithAttrs('as-popout-button', {
      'button-type': 'icon',
      'icon-name': 'user-options',
      'file-name': 'user.png',
      id: 'trincklePopout',
      'popout-placement': 'bottom-left',
      'tooltip-dict-key': 'tooltip.userOptions',
      'tooltip-default-text': 'User options',
      'tooltip-placement': 'bottom-left',
    });
    btn.appendChild(document.createElement(TrUserOptions.tag));
    return btn;
  }

  /**
   * Keep the clickthrough help link in sync as a *light-DOM* child of this
   * header, projected into the shadow `help-link` slot.
   *
   * It must be a real, connected, plain `<a target="_blank">` with no nested
   * interactive element, for the GrabCAD host:
   *   - scripted opens are blocked there (window.open / a detached anchor's
   *     .click() — what as-text-button's `link` does — do nothing), so the
   *     anchor has to be a genuine in-DOM element the user actually clicks;
   *   - a nested `<button>` (e.g. as-icon-button) has its own activation
   *     behaviour and would swallow the anchor's, so we use a bare `<img>`;
   *   - it must be light DOM — a click inside a shadow root is retargeted to the
   *     shadow host, so a shadow-DOM anchor never resolves as a link for the
   *     host. The home page's links work for exactly these reasons.
   *
   * The localized/flash tooltip lives in an as-tooltip-wrapper placed *inside*
   * the anchor, around the <img> only — the wrapper just slots the img, so the
   * anchor stays the clean light-DOM activatable element (no nested button, no
   * shadow boundary around the anchor itself).
   */
  private _updateHelpLink(): void {
    const clickthruUrl = asApp()?.getAttribute('clickthru-url') ?? '';
    const show = !isOnPrem && !!clickthruUrl;

    let link = this.querySelector<HTMLAnchorElement>(':scope > a.header-help-link');
    if (!show) { link?.remove(); return; }

    if (!link) {
      link = document.createElement('a');
      link.className = 'header-help-link';
      link.slot = 'help-link';
      link.target = '_blank';
      // Slotted-descendant img isn't reachable by the header's ::slotted rules,
      // so size it inline.
      link.innerHTML = /*html*/`<as-tooltip-wrapper
        show-on-hover
        placement="bottom-left"
        dict-key="header.help.tooltip"
        default-text="Need help? Click here for a tutorial."
        flash
        flash-duration="3000"
      ><img src="${iconPath('help') ?? ''}" alt="Help" style="height:1.8rem; width:auto;" /></as-tooltip-wrapper>`;
      this.appendChild(link);
    }
    link.href = clickthruUrl;
  }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
  }

  connectedCallback() {
    // Claim the 'header' refs slot: backs asHeader()/requireAsHeader() and
    // resolves any pending whenAsHeader().
    _registerSingleton('header', this);
    // The busy gate follows sysData.busy, so a header that connects while the
    // app is already busy starts gated.
    this._unbindBusy = bindElToSysData(this, 'busy', (busy) => {
      this._applyBusyGate(busy);
      return {};
    });
    // Check for empty slots after the component is connected to the DOM
    this._setupSlotListeners();

    // Use the sheet inside the web component constructor
    this.shadowRoot.adoptedStyleSheets = [sheet];

    const app = asApp();
    if (app) {
      this._envObserver = new MutationObserver(() => this._applyEnvSettings());
      this._envObserver.observe(app, { attributes: true, attributeFilter: ['env', 'clickthru-url'] });
    }

    // The constructor's render() ran before we were in the DOM, so asApp() was
    // null and neither the help link nor the env-dependent parts could resolve.
    // Sync them now we're connected.
    this._applyEnvSettings();
  }

  /**
   * Sets up listeners for slot content changes to add/remove hidden class
   */
  private _setupSlotListeners(): void {
    const slots = this.shadowRoot!.querySelectorAll('slot');
    slots.forEach(slot => {
      // Initial check
      this._checkSlotContent(slot);
      
      // Listen for changes
      slot.addEventListener('slotchange', () => {
        this._checkSlotContent(slot);
      });
    });
  }
  
  /**
   * Checks if a slot has assigned nodes and hides empty slots
   */
  private _checkSlotContent(slot: HTMLSlotElement): void {
    // Check for any non-whitespace text or element nodes
    const hasContent = Array.from(slot.assignedNodes()).some(node => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true;
      return false;
    });
    
    // Toggle the empty-slot class based on content
    if (hasContent) {
      slot.classList.remove('empty-slot');
    } else {
      slot.classList.add('empty-slot');
    }
  }

  disconnectedCallback() {
    // Release the 'header' refs slot, but only if we are still the one in it.
    _unregisterSingleton('header', this);
    this._unbindBusy?.();
    this._unbindBusy = null;

    if (this._envObserver) {
      this._envObserver.disconnect();
      this._envObserver = null;
    }
  }
}

customElements.define(TrHeader.tag, TrHeader);

// Lets wcQry('as-header') AND plain asHeader()
// resolve typed as TrHeader, the same as passing the class.
declare global {
  interface HTMLElementTagNameMap {
    'as-header': TrHeader;
    'as-user-options': TrUserOptions;
  }
}
